<template>
  <ReportModal :item-id="currentId" origin="publication"></ReportModal>
  <RateModal @rated="setUserRate" :item-id="currentId" origin="publication"></RateModal>
  <CommentModal
    @commentPosted="getPublicationComments"
    :item-id="currentId"
    origin="publication"
  ></CommentModal>

  <div class="my-10 flex flex-col">
    <div class="grid grid-cols-2 gap-5 max-lg:grid-cols-1">
      <div class="flex flex-col">
        <div>
          <video
            v-if="currentImage && isVideo(currentImage)"
            controls
            controlsList="nodownload"
            :src="currentImage"
            class="object-contain h-[450px] w-full"
          ></video>

          <img
            v-else
            @click="showSingle"
            :src="currentImage || placeholderSrc"
            @error="onImgError"
            alt="main-image"
            class="w-full h-[25rem] object-fill rounded-lg cursor-pointer"
            loading="lazy"
            decoding="async"
          />

          <vue-easy-lightbox
            :visible="visibleRef"
            :imgs="imgsRef"
            :index="indexRef"
            @hide="onHide"
          />
        </div>
        <div v-if="(detail?.images || []).length">
          <Splide :has-track="false" :options="imageOptions" aria-label="carouselBanner">
            <SplideTrack class="rounded-lg py-2">
              <SplideSlide v-for="(item, index) in detail?.images || []" :key="item.id || index">
                <div
                  class="h-32 w-full rounded-md overflow-hidden bg-black cursor-pointer"
                  @click="changeImage(index)"
                >
                  <video
                    v-if="isVideo(item?.path)"
                    :src="item?.path"
                    class="h-full w-full object-contain pointer-events-none"
                    muted
                    playsinline
                    preload="metadata"
                  ></video>

                  <img
                    v-else
                    :src="item?.path || placeholderSrc"
                    @error="onThumbError"
                    class="h-full w-full object-contain pointer-events-none"
                    loading="lazy"
                    decoding="async"
                    alt=""
                  />
                </div>
              </SplideSlide>
            </SplideTrack>
          </Splide>
        </div>
        <template v-if="detail?.business_account">
          <router-link
            :to="{ name: 'businessAccountsDetail', params: { id: detail?.business_account?.id } }"
            class="block mt-5"
          >
            <div
              class="bg-white rounded-md border border-[#E8E8E8] overflow-hidden transition hover:shadow-md"
            >
              <div class="flex items-center justify-between px-5 pt-5">
                <p class="text-main-color font-bold text-xl">
                  {{ $t('nav.businessAccount') }}
                </p>

                <div class="flex items-center gap-3">
                  <div
                    v-if="detail?.business_account?.subscription"
                    class="w-max rounded-lg py-1.5 px-8 font-bold max-md:py-2 max-md:px-4"
                    :class="getBSSubcriptionType(detail.business_account?.subscription)?.color"
                  >
                    {{ getBSSubcriptionType(detail.business_account?.subscription)?.name }}
                  </div>
                </div>
              </div>

              <div class="flex items-stretch mt-4">
                <div class="flex gap-4 px-5 pb-5 flex-1 min-w-0">
                  <div class="shrink-0">
                    <img
                      :src="detail?.business_account?.thumbnail || placeholderSrc"
                      @error="onThumbError"
                      class="h-32 w-32 rounded-md object-cover"
                      alt="business-account-thumbnail"
                    />
                  </div>

                  <div class="flex flex-col justify-center min-w-0 flex-1">
                    <p class="text-main-color font-bold text-xl truncate">
                      {{ detail?.business_account?.name }}
                    </p>

                    <p
                      v-if="detail?.business_account?.phone"
                      class="mt-1 text-[#333333] font-medium truncate"
                    >
                      {{ detail?.business_account?.phone }}
                    </p>

                    <p
                      v-if="detail?.business_account?.location"
                      class="mt-1 text-[#333333] truncate"
                    >
                      {{ $t('page.location') }}:
                      <span class="font-semibold">{{ detail?.business_account?.location }}</span>
                    </p>

                    <p
                      v-if="businessAccountCaption"
                      class="mt-1 text-[#666666] line-clamp-2 break-words"
                    >
                      {{ businessAccountCaption }}
                    </p>
                  </div>
                </div>

                <div
                  class="w-14 border-l border-[#E8E8E8] flex items-center justify-center shrink-0"
                >
                  <div
                    class="w-8 h-8 rounded-full border border-[#D9D9D9] flex items-center justify-center text-[#9B9B9B] text-[22px] leading-none"
                  >
                    <span class="-mt-[1px]">›</span>
                  </div>
                </div>
              </div>
            </div>
          </router-link>
        </template>
        <div v-if="advertisements.length > 0">
          <DetailBanner :content="advertisements"></DetailBanner>
        </div>
      </div>
      <div :class="{ 'bg-gradient-to-br from-[#ECECFE] to-[#D3D5FD] p-2 rounded': detail?.is_vip }">
        <div>
          <div class="border-b border-[#E3E3E3] mb-4">
            <p
              v-if="detail.is_new"
              class="font-light text-xs text-white py-1 px-3 bg-[#409DBA] w-20 mb-3"
            >
              {{ $t('page.newProduct') }}
            </p>

            <div class="flex items-center gap-3 mb-3">
              <div class="flex-1 min-w-0">
                <p
                  ref="titleEl"
                  class="w-full text-main-color font-bold leading-tight whitespace-nowrap overflow-hidden text-ellipsis"
                  :style="{ fontSize: titleFont + 'px' }"
                >
                  {{ titleText }}
                </p>
              </div>

              <button
                :disabled="loading"
                class="rounded-full bg-[#409DBA] h-[2.1875rem] w-[2.1875rem] flex justify-center items-center cursor-pointer flex-shrink-0"
                @click="likeAction"
              >
                <IconHeart :class="{ 'fill-white': isLiked }"></IconHeart>
              </button>
            </div>
            <div class="mb-2">
              <p>
                {{ $t('page.category') }}:
                <router-link
                  class="text-lg font-medium hover:text-accent-color"
                  :to="{ name: 'publications', query: { category: detail?.category_id } }"
                  >{{ detail?.category }}</router-link
                >
              </p>
            </div>

            <div class="flex items-center gap-5 mb-3">
              <p v-if="categoryDetail?.has_price" class="text-[#409DBA] font-semibold text-2xl">
                {{ formatNumber(detail.price) }} TMT
              </p>

              <div class="flex items-center gap-1">
                <IconEye></IconEye>
                <p class="text-main-color font-semibold text-sm">{{ detail.view_count }}</p>
              </div>
            </div>

            <div class="flex mb-2 font-light text-main-color text-base">
              <p v-if="categoryDetail?.has_address">{{ detail?.location }}</p>
              <div class="flex items-center">
                <div class="border-l mx-2 h-5"></div>
                <p>{{ detail?.date_created }}</p>
              </div>
            </div>
            <div>
              <a class="hover:text-accent-color" :href="`tel:${detail?.phone}`">{{
                detail?.phone
              }}</a>
            </div>
            <div class="flex gap-10 my-3">
              <div v-if="categoryDetail?.on_credit" class="flex items-center gap-2">
                {{ $t('page.onCredit') }} <IconChecked v-if="detail?.on_credit" />
                <IconUnchecked v-else />
              </div>
              <div v-if="categoryDetail?.on_exchange" class="flex items-center gap-2">
                {{ $t('page.onExchange') }} <IconChecked v-if="detail?.on_exchange" />
                <IconUnchecked v-else />
              </div>
            </div>
          </div>

          <div class="w-full">
            <div class="flex gap-1 mb-5">
              <IconComment></IconComment>
              <p>{{ detail.comments__count }} {{ $t('page.comments') }}</p>
            </div>
            <div class="mb-3 flex items-center gap-2">
              <p>{{ $t('page.yourRate') }}:</p>
              <VueStarRating
                :rounded-corners="true"
                :star-size="15"
                active-color="#ffb300"
                inactive-color="#f2f2f2"
                :border-width="2"
                border-color="#ffb300"
                :show-rating="false"
                :increment="1"
                :read-only="true"
                :rating="userRating"
              ></VueStarRating>
            </div>
            <p class="font-light break-all text-[#333333] text-lg w-96 mb-5">
              {{ detail.description }}
            </p>
            <div class="flex justify-end gap-5 mb-3">
              <button
                v-if="detail?.subscription"
                class="w-max rounded-lg px-8 font-bold"
                :class="getSubcriptionType?.color"
              >
                {{ getSubcriptionType?.name }}
              </button>
              <div
                v-if="detail?.is_vip"
                class="w-max px-8 bg-[#7951F9] rounded-lg text-white font-bold text-2xl text-end"
              >
                VIP
              </div>
            </div>
            <div class="bg-white divide-y rounded mb-5">
              <p class="text-main-color text-base font-semibold p-4">{{ $t('page.detail') }}</p>
              <InfoBlock :attributes="detail?.attributes" />
            </div>

            <div>
              <div class="flex gap-3 justify-between mb-3">
                <CommentButton v-if="detail?.allow_comments"></CommentButton>
                <RateButton></RateButton>
                <ReportButton></ReportButton>
              </div>
              <div class="w-full">
                <a
                  :href="`tel:${detail.phone}`"
                  class="w-full block text-center rounded-sm bg-main-color text-base py-4 text-white font-semibold"
                  >{{ $t('action.call') }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="comments.length">
      <p class="font-bold text-main-color text-3xl mt-20 mb-5 capitalize">
        {{ $t('page.comments') }}
      </p>

      <vue-easy-lightbox
        :visible="visibleAttachmentRef"
        :imgs="attachmentImgsRef"
        :index="0"
        @hide="visibleAttachmentRef = false"
      ></vue-easy-lightbox>

      <div
        class="border-b border-[#E0E0E0] pb-5 mb-3"
        v-for="(comment, index) in comments"
        :key="index"
      >
        <div class="flex flex-col mb-4">
          <div class="flex flex-col">
            <div class="flex flex-col mb-2">
              <div class="flex flex-col mb-3">
                <p class="text-lg text-main-color font-bold">{{ comment.username }}</p>
                <p class="test-[#333333] font-light text-sm">
                  {{ formatDate(comment.date_created) }}
                </p>
              </div>
              <p class="font-light text-lg text-[#333]">{{ comment.text }}</p>
            </div>
            <!-- <div class="flex items-center mb-1 md:col-start-3">
                            <button class="bg-[#717171] text-white font-light text-xs py-2 px-[1.375rem] rounded"
                                @click="openReplyCommentModal(comment)">Jogap
                                ýaz</button>
                        </div> -->

            <div
              v-if="comment.attachments && comment.attachments.length"
              class="flex gap-5 items-center overflow-x-auto mb-2"
            >
              <div v-for="attachment in comment.attachments" :key="attachment.id">
                <img
                  @click="showAttachment(attachment.path)"
                  :src="attachment.path"
                  alt="Attachment Image"
                  class="max-w-[156px] max-h-[90px] cursor-pointer"
                />
              </div>
            </div>
            <div class="flex gap-4 justify-end mb-2">
              <button class="flex gap-1 text-gray-600" @click="postCommentVote(comment.id, 1)">
                <IconLike :class="{ 'fill-[#a5ffab]': comment?.user_vote == 1 }" />
                {{ comment.up_vote_count }}
              </button>
              <button class="flex gap-1 text-gray-600" @click="postCommentVote(comment.id, -1)">
                <IconDislike :class="{ 'fill-[#ffb8b8]': comment?.user_vote == -1 }" />
                {{ comment.down_vote_count }}
              </button>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="reply in comment.replies" :key="reply.id" class="bg-[#e3e3e3] p-4 rounded">
              <div class="flex flex-col border-l border-solid border-main-color pl-3">
                <div class="flex items-center gap-4">
                  <p class="font-bold">{{ $t('page.owner') }}</p>
                  <p class="text-sm">{{ reply.date_created }}</p>
                </div>

                <div class="mt-2">
                  <p>{{ reply.text }}</p>
                </div>
                <div
                  v-if="reply.attachments && reply.attachments.length"
                  class="flex gap-5 items-center overflow-x-auto mt-3"
                >
                  <div
                    v-for="attachment in reply.attachments"
                    :key="`reply-attachment-${attachment.id}`"
                  >
                    <img
                      @click="showAttachment(attachment.path)"
                      :src="attachment.path"
                      alt="Reply Attachment Image"
                      class="max-w-[156px] max-h-[90px] cursor-pointer"
                    />
                  </div>
                </div>
              </div>
              <div class="flex gap-4 justify-end">
                <button class="flex gap-1 text-gray-600" @click="postCommentVote(reply.id, 1)">
                  <IconLike :class="{ 'fill-[#a5ffab]': reply?.user_vote == 1 }" />
                  {{ reply.up_vote_count }}
                </button>
                <button class="flex gap-1 text-gray-600" @click="postCommentVote(reply.id, -1)">
                  <IconDislike :class="{ 'fill-[#ffb8b8]': reply?.user_vote == -1 }" />
                  {{ reply.down_vote_count }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="mt-5"
      v-if="
        similarPublications.length > 0 &&
        !(
          recommendations.mall_stores &&
          recommendations.exhibition_stores &&
          recommendations.business_accounts
        )
      "
    >
      <p class="font-semibold text-main-color text-3xl mb-5">
        {{ $t('page.similarPublications') }}
      </p>

      <div class="grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2">
        <router-link
          v-for="(pub, index) in similarPublications"
          :key="index"
          :to="{ name: 'publicationDetail', params: { id: pub.id } }"
        >
          <PublicationCard :detail="pub"> </PublicationCard>
        </router-link>
      </div>
    </div>
    <p
      v-if="
        recommendations.mall_stores.length > 0 &&
        recommendations.exhibition_stores.length > 0 &&
        recommendations.business_accounts.length > 0
      "
      class="mt-10 font-semibold text-main-color text-3xl"
    >
      {{ $t('page.selecteds') }}
    </p>
    <div class="mt-5" v-if="recommendations?.mall_stores?.length > 0">
      <div class="grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2">
        <router-link
          v-for="item in recommendations?.mall_stores"
          :key="item?.id"
          :to="{ name: 'storeDetail', params: { id: item?.id } }"
        >
          <StoreCard :detail="item"></StoreCard>
        </router-link>
      </div>
    </div>

    <div class="mt-5" v-if="recommendations?.exhibition_stores?.length > 0">
      <div class="grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2">
        <router-link
          v-for="item in recommendations?.exhibition_stores"
          :key="item?.id"
          :to="{ name: 'foodRestaurantDetail', params: { id: item?.id } }"
        >
          <StoreCard :detail="item"> </StoreCard>
        </router-link>
      </div>
    </div>

    <div class="mt-5" v-if="recommendations?.business_accounts?.length > 0">
      <div class="grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2">
        <router-link
          v-for="item in recommendations?.business_accounts"
          :key="item.id"
          :to="{ name: 'businessAccountsDetail', params: { id: item.id } }"
        >
          <BusinessAccountCard :detail="item"> </BusinessAccountCard>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import { i18n } from '@/i18n';
import VueEasyLightbox from 'vue-easy-lightbox';
import VueStarRating from 'vue-star-rating';
import { ref } from 'vue';
import Modal from '@cmp/Modal.vue';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { Field, Form, ErrorMessage } from 'vee-validate';
import { formatDate, formattedHour, formatNumber } from '@/helpers';
import DetailBanner from '@cmp/DetailBanner.vue';
import PublicationCard from '@cmp/publication/PublicationCard.vue';
import StoreCard from '@cmp/mall/StoreCard.vue';
import BusinessAccountCard from '@cmp/media/BusinessAccountCard.vue';
import IconHeart from '@icons/IconHeart.vue';
import IconEye from '@icons/IconEye.vue';
import IconComment from '@icons/IconComment.vue';
import IconClip from '@icons/IconClip.vue';
import IconTrash from '@icons/IconTrash.vue';
import IconChecked from '@icons/IconChecked.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import InlineLoader from '@cmp/InlineLoader.vue';
import ReportButton from '@cmp/modals/buttons/ReportButton.vue';
import CommentButton from '@cmp/modals/buttons/CommentButton.vue';
import RateButton from '@cmp/modals/buttons/RateButton.vue';
import ReportModal from '@cmp/modals/ReportModal.vue';
import CommentModal from '@cmp/modals/CommentModal.vue';
import RateModal from '@cmp/modals/RateModal.vue';
import IconLike from '@icons/IconLike.vue';
import IconDislike from '@icons/IconDislike.vue';
import { isVideo } from '@/helpers';
import InfoBlock from '@cmp/InfoBlock.vue';

export default {
  name: 'publicationDetail',
  components: {
    IconClip,
    IconTrash,
    InlineLoader,
    IconChecked,
    IconUnchecked,
    DetailBanner,
    IconHeart,
    IconEye,
    IconComment,
    Splide,
    SplideSlide,
    SplideTrack,
    Field,
    Form,
    ErrorMessage,
    VueEasyLightbox,
    Modal,
    PublicationCard,
    BusinessAccountCard,
    StoreCard,
    ReportButton,
    ReportModal,
    RateModal,
    CommentModal,
    CommentButton,
    RateButton,
    VueStarRating,
    IconLike,
    IconDislike,
    InfoBlock,
  },
  data() {
    return {
      placeholderSrc: '/placeholder-publication.png',
      titleFont: 30, // старт примерно как text-3xl
      titleFontMax: 30,
      titleFontMin: 18,
      loading: false,
      currentId: null,
      detail: {},
      comments: [],
      advertisements: [],
      visibleRef: false,
      visibleAttachmentRef: false,
      indexRef: 0,
      imgsRef: [],
      attachmentImgsRef: [],
      currentImage: null,
      imageOptions: {
        perPage: 3,
        perMove: 1,
        gap: '10px',
      },
      commentModal: false,
      similarPublications: [],
      isLiked: false,
      categoryDetail: {},
      formComment: {
        text: '',
        attachments: [],
      },
      files: [],
      fileUrls: [],
      fileIds: [],
      categoryQuery: null,
      recommendations: {
        mall_stores: [],
        exhibition_stores: [],
        business_accounts: [],
      },
      userRating: 0,
    };
  },
  watch: {
    '$route.params.id': {
      //   immediate: true,
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.currentId = newValue;
          this.getPublicationDetail();
          this.getPublicationComments();
        }
      },
    },
    // ✅ когда заголовок поменялся — подгоняем шрифт
    titleText() {
      this.$nextTick(() => this.fitTitle());
    },
  },

  mounted() {
    window.addEventListener('resize', this.fitTitle);
    this.fitTitle();
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.fitTitle);
  },

  methods: {
    isVideo,
    formatDate,
    formattedHour,
    formatNumber,
    setUserRate(value) {
      this.userRating = value;
    },
    buildImgsRef() {
      const images = Array.isArray(this.detail?.images) ? this.detail.images : [];
      this.imgsRef = images.map(i => i?.path).filter(p => p && !this.isVideo(p));
    },
    syncIndexRef() {
      const idx = this.imgsRef.findIndex(p => p === this.currentImage);
      this.indexRef = idx >= 0 ? idx : 0;
    },
    onShow() {
      if (
        !this.currentImage ||
        this.currentImage === this.placeholderSrc ||
        this.isVideo(this.currentImage)
      )
        return;

      this.buildImgsRef();
      this.syncIndexRef();
      this.visibleRef = true;
    },
    onImgError(e) {
      if (e?.target?.src && !e.target.src.endsWith(this.placeholderSrc)) {
        e.target.src = this.placeholderSrc;
      }
      this.currentImage = this.placeholderSrc;
    },

    onThumbError(e) {
      if (e?.target?.src && !e.target.src.endsWith(this.placeholderSrc)) {
        e.target.src = this.placeholderSrc;
      }
    },

    onHide() {
      this.visibleRef = false;
    },

    showAttachment(img) {
      if (!img) return;

      this.visibleAttachmentRef = true;
      this.attachmentImgsRef = [img];
    },

    showSingle() {
      this.onShow();
    },
    changeImage(index) {
      const images = Array.isArray(this.detail?.images) ? this.detail.images : [];
      this.currentImage = images?.[index]?.path || this.placeholderSrc;
      this.buildImgsRef();
      this.syncIndexRef();
    },

    openCommentModal() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      this.commentModal = true;
    },
    closeCommentModal() {
      this.commentModal = false;
      this.formComment = {
        text: '',
        attachments: [],
      };
    },
    remove(i) {
      this.files.splice(i, 1);
      this.fileIds.splice(i, 1);
    },
    async onFileChange() {
      for (var x = 0; x < this.$refs.file.files.length; x++) {
        const fileForm = new FormData();
        const f = this.$refs.file.files[x];
        fileForm.append('path', f);
        this.loading = true;
        const response = await api.post('files/images/', fileForm, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response && response.status === 201) {
          this.files.push({
            detail: this.$refs.file.files[x],
            path: URL.createObjectURL(this.$refs.file.files[x]),
            isUploaded: true,
          });
          this.fileIds.push(response.data.id);
        } else {
          this.$toast.warning(`${i18n.global.t('warning.UPLOAD_FAILED')}`);
        }
        this.loading = false;
      }
    },

    async likeAction() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        this.loading = true;
        const response = await api.post(`publications/publications/${this.currentId}/like/`, {
          is_liked: !this.isLiked,
        });
        this.isLiked = !this.isLiked;
        this.$store.getLiked('publications');
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async fitTitle() {
      await this.$nextTick();

      const el = this.$refs.titleEl;
      if (!el) return;

      // сброс на максимум
      let size = this.titleFontMax;
      el.style.fontSize = size + 'px';

      // иногда flex-элементы без min-w-0 не считают ширину корректно — ты уже добавил min-w-0 выше
      const fits = () => el.scrollWidth <= el.clientWidth;

      while (!fits() && size > this.titleFontMin) {
        size -= 1;
        el.style.fontSize = size + 'px';
      }

      this.titleFont = size;
    },

    async getPublicationAd() {
      try {
        const response = await api.get('commercials/advertisements/', {
          params: {
            type: 204,
            location: this.getLocation.id,
            publication_categories: this.detail?.category_id,
          },
        });
        this.advertisements = response.data;
        console.log(this.advertisements);
      } catch (e) {
        console.log(e);
      }
    },
    async getCategoryDetail() {
      try {
        const response = await api.get(`publications/categories/${this.categoryQuery}/`);
        this.categoryDetail = response.data;
      } catch (e) {
        console.log(e);
      }
    },

    async getPublicationDetail() {
      try {
        const response = await api.get(`publications/publications/${this.currentId}/`);
        this.detail = response.data;
        this.isLiked = this.detail?.is_liked;

        this.categoryQuery = this.detail?.category_id;
        await this.getCategoryDetail();

        this.fitTitle();

        const medias = Array.isArray(this.detail?.images) ? this.detail.images : [];
        this.currentImage = medias?.[0]?.path || this.placeholderSrc;

        this.buildImgsRef();
        this.syncIndexRef();

        this.userRating = this.detail?.user_rating || 0;
        this.getPublicationAd();
        this.getPublicationSimilars();
        this.getPostRecommendations();
      } catch (e) {
        console.log(e);
      }
    },
    async getPublicationComments() {
      try {
        const response = await api.get(`publications/publications/${this.currentId}/comment/`);
        this.comments = response.data.results;
      } catch (e) {
        console.log(e);
      }
    },
    async getPublicationSimilars() {
      try {
        const response = await api.get(`publications/publications/`, {
          params: { category: this.detail.category_id, page: 1, location: this.getLocation.id },
        });
        this.similarPublications = response.data.results;
      } catch (e) {
        console.log(e);
      }
    },

    async postPublicationComment() {
      try {
        const body = {
          text: this.formComment.text,
          attachments: this.fileIds,
        };
        const url = `publications/publications/${this.currentId}/comment/`;
        const response = await api.post(url, body);
        this.$toast.info(`${i18n.global.t('warning.COMMENT_POSTED')}`);
        this.closeCommentModal();
      } catch (e) {
        console.log(e);
      }
    },
    async getPostRecommendations() {
      try {
        const response = await api.get(
          `publications/categories/${this.detail?.category_id}/recommendations/`,
        );
        this.recommendations = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    getBSSubcriptionType(subscription) {
      switch (subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          break;
      }
    },
    async postCommentVote(id, value) {
      try {
        const response = await api.post(`comments/${id}/vote/`, { vote: value });
        await this.getPublicationComments();
      } catch (e) {
        console.log(e);
      }
    },
  },

  computed: {
    businessAccountCaption() {
      const ba = this.detail?.business_account || {};
      const locale = this.$i18n?.locale || 'ru';

      if (locale === 'ru') {
        return ba.caption_ru || ba.caption || ba.caption_tm || '';
      }

      if (locale === 'tk') {
        return ba.caption_tm || ba.caption || ba.caption_ru || '';
      }

      return ba.caption || ba.caption_ru || ba.caption_tm || '';
    },

    getLocation() {
      return JSON.parse(localStorage.getItem('selectedLocation')) || 1;
    },
    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          break;
      }
    },
    titleText() {
      if (this.detail?.name) return this.detail.name;
      const props = Array.isArray(this.detail?.title_properties)
        ? this.detail.title_properties
        : [];
      return props
        .map(p => p?.value)
        .filter(Boolean)
        .join(' ');
    },
  },
  created() {
    this.currentId = this.$route.params.id;
    this.getPublicationDetail();
    this.getPublicationComments();
  },
};
</script>
